// ============================================================
// STOCKO - Système de notifications push
// ============================================================

import { supabase } from './supabase'

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY

// Convertir la clé VAPID en Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

// Demander la permission et enregistrer le token
export async function activerNotifications(boutiqueId) {
  if (!('Notification' in window)) {
    return { success: false, reason: 'not_supported' }
  }
  if (!('serviceWorker' in navigator)) {
    return { success: false, reason: 'no_sw' }
  }

  try {
    // Demander la permission
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return { success: false, reason: 'denied' }
    }

    // Enregistrer le service worker
    const registration = await navigator.serviceWorker.ready

    // S'abonner aux notifications push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
    })

    // Sauvegarder le token dans Supabase
    const token = JSON.stringify(subscription)
    await supabase.from('notification_tokens').upsert({
      boutique_id: boutiqueId,
      token
    }, { onConflict: 'boutique_id,token' })

    return { success: true }
  } catch (err) {
    console.error('Erreur notifications:', err)
    return { success: false, reason: err.message }
  }
}

// Vérifier si les notifications sont activées
export async function notificationsActivees() {
  if (!('Notification' in window)) return false
  return Notification.permission === 'granted'
}

// Envoyer une notification locale (sans serveur)
export function notifierLocal(titre, body, icon = '/icons/icon-192x192.png') {
  if (Notification.permission !== 'granted') return
  
  navigator.serviceWorker.ready.then(registration => {
    registration.showNotification(titre, {
      body,
      icon,
      badge: '/icons/icon-96x96.png',
      vibrate: [200, 100, 200],
      tag: 'stocko-alert',
      renotify: true,
      data: { url: '/' }
    })
  })
}

// Vérifier les alertes et envoyer les notifications
export async function verifierAlertes(produits, lang = 'fr') {
  if (Notification.permission !== 'granted') return

  const aujourd = new Date()
  aujourd.setHours(0, 0, 0, 0)

  for (const p of produits) {
    // ALERTE STOCK FAIBLE
    if (p.quantite > 0 && p.quantite <= p.seuil_alerte) {
      notifierLocal(
        lang === 'fr' ? `⚠️ Stock faible — ${p.nom}` : `⚠️ Low stock — ${p.nom}`,
        lang === 'fr'
          ? `Il ne reste que ${p.quantite} ${p.unite}. Pensez à commander.`
          : `Only ${p.quantite} ${p.unite} left. Consider reordering.`
      )
    }

    // ALERTE RUPTURE
    if (p.quantite === 0) {
      notifierLocal(
        lang === 'fr' ? `🚨 Rupture de stock — ${p.nom}` : `🚨 Out of stock — ${p.nom}`,
        lang === 'fr'
          ? `${p.nom} est épuisé ! Commander immédiatement.`
          : `${p.nom} is out of stock! Order immediately.`
      )
    }

    // ALERTES PÉREMPTION
    if (p.est_perissable && p.date_peremption) {
      const dateExp = new Date(p.date_peremption)
      dateExp.setHours(0, 0, 0, 0)
      const diffJours = Math.ceil((dateExp - aujourd) / (1000 * 60 * 60 * 24))

      if (diffJours === 30) {
        notifierLocal(
          lang === 'fr' ? `📅 Péremption dans 1 mois — ${p.nom}` : `📅 Expiry in 1 month — ${p.nom}`,
          lang === 'fr'
            ? `${p.nom} expire le ${dateExp.toLocaleDateString('fr-FR')}. Planifiez vos ventes.`
            : `${p.nom} expires on ${dateExp.toLocaleDateString('en-GB')}. Plan your sales.`
        )
      } else if (diffJours === 7) {
        notifierLocal(
          lang === 'fr' ? `⚠️ Péremption dans 1 semaine — ${p.nom}` : `⚠️ Expiry in 1 week — ${p.nom}`,
          lang === 'fr'
            ? `${p.nom} expire dans 7 jours. Accélérez les ventes ou baissez le prix.`
            : `${p.nom} expires in 7 days. Speed up sales or lower the price.`
        )
      } else if (diffJours === 1) {
        notifierLocal(
          lang === 'fr' ? `🚨 Expire demain — ${p.nom}` : `🚨 Expires tomorrow — ${p.nom}`,
          lang === 'fr'
            ? `${p.nom} expire DEMAIN. Vendez au plus vite !`
            : `${p.nom} expires TOMORROW. Sell as fast as possible!`
        )
      } else if (diffJours === 0) {
        notifierLocal(
          lang === 'fr' ? `☠️ Expire AUJOURD'HUI — ${p.nom}` : `☠️ Expires TODAY — ${p.nom}`,
          lang === 'fr'
            ? `${p.nom} expire aujourd'hui. Retirez-le du stock si non vendu.`
            : `${p.nom} expires today. Remove from stock if unsold.`
        )
      } else if (diffJours < 0) {
        notifierLocal(
          lang === 'fr' ? `🗑️ Produit périmé — ${p.nom}` : `🗑️ Expired product — ${p.nom}`,
          lang === 'fr'
            ? `${p.nom} est périmé depuis ${Math.abs(diffJours)} jour(s). Retirez-le.`
            : `${p.nom} expired ${Math.abs(diffJours)} day(s) ago. Remove it.`
        )
      }
    }
  }
}
